/**
 * Turns a specifier from a PR diff (a relative import, an aliased import, or a repository path)
 * into the files it can name. Pure string work with no I/O, so the API route, which lists
 * directories and the tree, and the diff's click handler, which picks the specifier out of a line,
 * share one definition of what a specifier means.
 */

/**
 * Extensions a module resolver appends to an extensionless specifier, tried in this order.
 * Extensionless imports are mostly a JavaScript and TypeScript convention, so that family comes
 * first; Ruby's `require_relative` and documentation links are the other common sources.
 */
export const REPO_FILE_SOURCE_EXTENSIONS = [
  'ts',
  'tsx',
  'd.ts',
  'js',
  'jsx',
  'mts',
  'cts',
  'mjs',
  'cjs',
  'vue',
  'svelte',
  'astro',
  'json',
  'css',
  'scss',
  'sass',
  'less',
  'rb',
  'md',
  'mdx',
] as const;

/** Python bindings name explicit `.py` paths; recognize them without expanding other imports. */
const KNOWN_SOURCE_EXTENSIONS = [...REPO_FILE_SOURCE_EXTENSIONS, 'py'] as const;

/** Files a directory specifier resolves to, in the order they are tried. */
export const REPO_FILE_INDEX_NAMES: readonly string[] = REPO_FILE_SOURCE_EXTENSIONS.map(
  (extension) => `index.${extension}`,
);

/**
 * TypeScript's ESM imports name the emitted JavaScript file, so a `.js` specifier is usually
 * written in a `.ts` source.
 */
const TYPESCRIPT_SOURCE_EXTENSIONS: Readonly<Record<string, readonly string[]>> = {
  js: ['ts', 'tsx'],
  jsx: ['tsx'],
  mjs: ['mts'],
  cjs: ['cts'],
};

/**
 * Prefixes that stand for a source root in someone's build config. The rest is matched as a path
 * suffix instead of parsing tsconfig paths, because cubic renders other people's repositories and
 * their alias tables are unknown.
 */
const ALIAS_PREFIXES = ['@/', '~/', '#/'] as const;

const URL_SCHEME = /^[a-z][a-z\d+.-]*:/i;
const SCOPED_PACKAGE = /^@[^/]+\//;
const SLASH_BETWEEN_SEGMENTS = /[^/]\/[^/]/;
const TRAILING_SENTENCE_PUNCTUATION = /[.,;:!?]+$/;
const WORD_CHARACTER = /[\p{L}\p{N}]/u;
const QUOTES = new Set(["'", '"', '`']);
const UNQUOTED_TOKEN_DELIMITERS = new Set([
  "'",
  '"',
  '`',
  '(',
  ')',
  '<',
  '>',
  '{',
  '}',
  ',',
  ';',
  '=',
  '|',
  '*',
]);

export type RepoFileSpecifier =
  /** `./` or `../`, resolved against the importing file's directory; empty at the root. */
  | { kind: 'relative'; path: string }
  /** An `@/`, `~/` or `#/` alias, whose rest names a file at any depth of the tree. */
  | { kind: 'alias'; suffix: string }
  /** A path with a `/` or a Python filename and no prefix: exact first, then a suffix like an alias. */
  | { kind: 'bare-path'; path: string }
  /** A package name, such as `react` or `@scope/name` with any subpath, not a repository file. */
  | { kind: 'package' };

/**
 * Reads a specifier as written in `fromPath`. Null when it can never name a file in the
 * repository: empty, a URL or `node:` builtin, an absolute or Windows path, or a path that climbs
 * above the repository root.
 */
export function parseRepoFileSpecifier(
  specifier: string,
  fromPath: string,
): RepoFileSpecifier | null {
  const text = stripQueryAndFragment(specifier);

  if (
    text.length === 0 ||
    hasControlCharacter(text) ||
    text.startsWith('/') ||
    text.includes('\\') ||
    URL_SCHEME.test(text)
  ) {
    return null;
  }

  if (text === '.' || text === '..' || text.startsWith('./') || text.startsWith('../')) {
    const path = normalizeSegments([...directorySegments(fromPath), ...text.split('/')]);

    return path === null ? null : { kind: 'relative', path };
  }

  const aliasPrefix = ALIAS_PREFIXES.find((prefix) => text.startsWith(prefix));

  if (aliasPrefix) {
    const suffix = normalizeSegments(text.slice(aliasPrefix.length).split('/'));

    return suffix ? { kind: 'alias', suffix } : null;
  }

  if (
    (!text.includes('/') && findSourceExtension(text)?.toLowerCase() !== 'py') ||
    SCOPED_PACKAGE.test(text)
  ) {
    return { kind: 'package' };
  }

  const path = normalizeSegments(text.split('/'));

  return path ? { kind: 'bare-path', path } : null;
}

/**
 * The file names a path's last segment can stand for, in resolution order: the name as written,
 * then the TypeScript sources of an emitted `.js` name, then each source extension when the name
 * has none.
 */
export function buildRepoFileNameCandidates(name: string): string[] {
  const extension = findSourceExtension(name);

  if (extension === null) {
    return [
      name,
      ...REPO_FILE_SOURCE_EXTENSIONS.map((sourceExtension) => `${name}.${sourceExtension}`),
    ];
  }

  const stem = name.slice(0, name.length - extension.length - 1);
  const sources = TYPESCRIPT_SOURCE_EXTENSIONS[extension.toLowerCase()] ?? [];

  return [name, ...sources.map((source) => `${stem}.${source}`)];
}

/** Splits a repository path into its directory (empty at the root) and its last segment. */
export function splitRepoFilePath(path: string): { directory: string; name: string } {
  const index = path.lastIndexOf('/');

  return index === -1
    ? { directory: '', name: path }
    : { directory: path.slice(0, index), name: path.slice(index + 1) };
}

export function joinRepoFilePath(directory: string, name: string): string {
  return directory ? `${directory}/${name}` : name;
}

export type RepoFileTreeMatch =
  | { kind: 'found'; path: string }
  | { kind: 'missing'; tried: string[] };

/**
 * Finds the blob an alias or bare path names in a commit's tree. A bare path is first looked up
 * exactly; otherwise every candidate is matched as a suffix at a `/` boundary, and the match
 * closest to `fromPath` wins, so a monorepo resolves `@/lib/utils` inside the importing app.
 * Ties favor the earlier candidate before falling back to path order.
 */
export function resolveRepoFileInTree(
  specifier: Extract<RepoFileSpecifier, { kind: 'alias' | 'bare-path' }>,
  blobPaths: ReadonlySet<string>,
  fromPath: string,
): RepoFileTreeMatch {
  const candidates = buildRepoFilePathCandidates(
    specifier.kind === 'alias' ? specifier.suffix : specifier.path,
  );
  const exactCandidates = specifier.kind === 'bare-path' ? candidates : [];
  const exact = exactCandidates.find((candidate) => blobPaths.has(candidate));

  if (exact) {
    return { kind: 'found', path: exact };
  }

  const suffixMatch = findClosestSuffixMatch(candidates, blobPaths, fromPath);

  if (suffixMatch) {
    return { kind: 'found', path: suffixMatch };
  }

  return {
    kind: 'missing',
    tried: [...exactCandidates, ...candidates.map((candidate) => `**/${candidate}`)],
  };
}

/**
 * The characters of the path-like specifier at `offset` in a line of source: the inside of the
 * quoted string the offset falls in, quotes included, or the unquoted token under it. Null off a
 * path. The diff underlines exactly this range, and a cmd-click on it opens these characters.
 */
export function findRepoFileSpecifierRange(
  lineText: string,
  offset: number,
  fromPath: string,
): LineRange | null {
  const quoted = findQuotedStrings(lineText).find(
    (span) => span.start <= offset && offset <= span.end,
  );

  if (quoted) {
    return isRepoFilePathLike(quoted.text, fromPath, true)
      ? { start: quoted.start + 1, end: quoted.end }
      : null;
  }

  const token = findUnquotedTokenAt(lineText, offset);

  return token && isRepoFilePathLike(lineText.slice(token.start, token.end), fromPath)
    ? token
    : null;
}

/**
 * Whether a cmd-click should open the specifier. Stricter than what the route resolves, because a
 * bare name like `react` resolves to nothing and a slash with nothing on one side, as in `<br/>`,
 * is markup rather than a path. Relative paths ending in `/` can name a directory's index.
 * Bare Python filenames are paths only when quoted, so `obj.py` remains a property access.
 */
function isRepoFilePathLike(text: string, fromPath: string, quoted = false): boolean {
  const specifier = parseRepoFileSpecifier(text, fromPath);

  if (specifier === null || specifier.kind === 'package') {
    return false;
  }

  const path = stripQueryAndFragment(text);

  return (
    SLASH_BETWEEN_SEGMENTS.test(path) ||
    (specifier.kind === 'relative' && path.endsWith('/')) ||
    (quoted && specifier.kind === 'bare-path' && findSourceExtension(path)?.toLowerCase() === 'py')
  );
}

/**
 * The repository paths `path` can name, in resolution order: the file names its last segment
 * stands for, then the index files of the directory it names. The root names only index files.
 * A Python module names its `.py` file first, then its package's `__init__.py`.
 */
export function buildRepoFilePathCandidates(path: string): string[] {
  const { directory, name } = splitRepoFilePath(path);

  if (findSourceExtension(name)?.toLowerCase() === 'py') {
    return name === '__init__.py' ? [path] : [path, `${path.slice(0, -3)}/__init__.py`];
  }

  const files =
    path === ''
      ? []
      : buildRepoFileNameCandidates(name).map((candidate) =>
          joinRepoFilePath(directory, candidate),
        );

  return [...files, ...REPO_FILE_INDEX_NAMES.map((indexName) => joinRepoFilePath(path, indexName))];
}

function findClosestSuffixMatch(
  candidates: readonly string[],
  blobPaths: ReadonlySet<string>,
  fromPath: string,
): string | null {
  const candidateRanks = new Map<string, number>();

  candidates.forEach((candidate, index) => {
    if (!candidateRanks.has(candidate)) {
      candidateRanks.set(candidate, index);
    }
  });

  const fromDirectory = directorySegments(fromPath);
  let best: { path: string; sharedDepth: number; depth: number; rank: number } | null = null;

  for (const path of blobPaths) {
    const rank = findCandidateSuffixRank(path, candidateRanks);

    if (rank === null) {
      continue;
    }

    const segments = path.split('/');
    const match = {
      path,
      sharedDepth: countSharedLeadingSegments(segments.slice(0, -1), fromDirectory),
      depth: segments.length,
      rank,
    };

    if (best === null || isCloserMatch(match, best)) {
      best = match;
    }
  }

  return best?.path ?? null;
}

/** Earliest candidate matching the whole path or a suffix starting at a `/`. */
function findCandidateSuffixRank(
  path: string,
  candidates: ReadonlyMap<string, number>,
): number | null {
  let rank = candidates.get(path) ?? null;

  for (let index = path.indexOf('/'); index !== -1; index = path.indexOf('/', index + 1)) {
    const candidateRank = candidates.get(path.slice(index + 1));

    if (candidateRank !== undefined && (rank === null || candidateRank < rank)) {
      rank = candidateRank;
    }
  }

  return rank;
}

/** Break remaining ties in code-unit order so every runtime agrees on the pick. */
function isCloserMatch(
  candidate: { path: string; sharedDepth: number; depth: number; rank: number },
  incumbent: { path: string; sharedDepth: number; depth: number; rank: number },
): boolean {
  if (candidate.sharedDepth !== incumbent.sharedDepth) {
    return candidate.sharedDepth > incumbent.sharedDepth;
  }

  if (candidate.depth !== incumbent.depth) {
    return candidate.depth < incumbent.depth;
  }

  if (candidate.rank !== incumbent.rank) {
    return candidate.rank < incumbent.rank;
  }

  return candidate.path < incumbent.path;
}

function countSharedLeadingSegments(left: readonly string[], right: readonly string[]): number {
  let count = 0;

  while (count < left.length && count < right.length && left[count] === right[count]) {
    count += 1;
  }

  return count;
}

/** The characters `[start, end)` of a line. */
export type LineRange = { start: number; end: number };

type QuotedString = {
  /** Index of the opening quote. */
  start: number;
  /** Index of the closing quote. */
  end: number;
  text: string;
};

function findQuotedStrings(line: string): QuotedString[] {
  const strings: QuotedString[] = [];
  let index = 0;

  while (index < line.length) {
    const character = line[index] ?? '';

    if (!QUOTES.has(character) || isApostropheInWord(line, index)) {
      index += 1;
      continue;
    }

    const end = findClosingQuote(line, character, index + 1);

    if (end === -1) {
      index += 1;
      continue;
    }

    strings.push({ start: index, end, text: line.slice(index + 1, end) });
    index = end + 1;
  }

  return strings;
}

/** `don't` in a comment is prose, not the start of a string. */
function isApostropheInWord(line: string, index: number): boolean {
  return (
    line[index] === "'" &&
    WORD_CHARACTER.test(line[index - 1] ?? '') &&
    WORD_CHARACTER.test(line[index + 1] ?? '')
  );
}

function findClosingQuote(line: string, quote: string, from: number): number {
  for (let index = from; index < line.length; index += 1) {
    if (line[index] === quote && !isEscaped(line, index)) {
      return index;
    }
  }

  return -1;
}

function isEscaped(line: string, index: number): boolean {
  let backslashes = 0;

  for (let cursor = index - 1; cursor >= 0 && line[cursor] === '\\'; cursor -= 1) {
    backslashes += 1;
  }

  return backslashes % 2 === 1;
}

function findUnquotedTokenAt(line: string, offset: number): LineRange | null {
  if (offset < 0 || offset >= line.length || isTokenDelimiter(line[offset] ?? '')) {
    return null;
  }

  let start = offset;
  let end = offset + 1;

  while (start > 0 && !isTokenDelimiter(line[start - 1] ?? '')) {
    start -= 1;
  }

  while (end < line.length && !isTokenDelimiter(line[end] ?? '')) {
    end += 1;
  }

  const token = line.slice(start, end).replace(TRAILING_SENTENCE_PUNCTUATION, '');
  const tokenEnd = start + token.length;

  return tokenEnd > offset ? { start, end: tokenEnd } : null;
}

function isTokenDelimiter(character: string): boolean {
  return UNQUOTED_TOKEN_DELIMITERS.has(character) || /\s/.test(character);
}

/** Drops a `?raw`-style query or `#fragment`. A leading `#` is the `#/` alias, not a fragment. */
function stripQueryAndFragment(text: string): string {
  const cuts = [text.indexOf('?'), text.indexOf('#', 1)].filter((index) => index !== -1);

  return cuts.length > 0 ? text.slice(0, Math.min(...cuts)) : text;
}

function hasControlCharacter(text: string): boolean {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);

    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) {
      return true;
    }
  }

  return false;
}

/** Resolves `.` and `..` segments; empty at the root, null only above the root. */
function normalizeSegments(segments: readonly string[]): string | null {
  const resolved: string[] = [];

  for (const segment of segments) {
    if (segment === '' || segment === '.') {
      continue;
    }

    if (segment === '..') {
      if (resolved.length === 0) {
        return null;
      }

      resolved.pop();
      continue;
    }

    resolved.push(segment);
  }

  return resolved.join('/');
}

function directorySegments(path: string): string[] {
  return path
    .split('/')
    .filter((segment) => segment.length > 0)
    .slice(0, -1);
}

/** The known source extension a file name ends in, or null when it has none. */
function findSourceExtension(name: string): string | null {
  const lowerName = name.toLowerCase();

  for (const extension of KNOWN_SOURCE_EXTENSIONS) {
    if (lowerName.endsWith(`.${extension}`) && lowerName.length > extension.length + 1) {
      return name.slice(name.length - extension.length);
    }
  }

  return null;
}
